import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import BookConsultationButton from "@/components/BookConsultationButton";
import { 
  TreePine, MapPin, Star, TrendingUp, Users, Target, 
  CheckCircle, ArrowRight, Phone, Calendar, Leaf,
  Camera, Sun, BarChart3, Sparkles, Download, FileText
} from "lucide-react";

const LandscapingMarketingPage = () => {

  useEffect(() => {
    document.title = "Landscaping Marketing Dallas | Lawn Care SEO | Digital Media Ninja";
  }, []);

  const stats = [
    { value: "Leads", label: "Seasonal Campaigns", icon: TrendingUp },
    { value: "Landscapers", label: "Industry Focus", icon: TreePine },
    { value: "Projects", label: "Higher-Value Jobs", icon: Target },
    { value: "Booking", label: "Schedule-Filling Funnels", icon: BarChart3 }
  ];

  const services = [
    { icon: MapPin, title: "Local SEO for Landscapers", desc: "Rank #1 for 'landscaper near me' and lawn care searches in Dallas-Fort Worth." },
    { icon: Camera, title: "Visual Portfolio Marketing", desc: "Showcase stunning before/after photos and video walkthroughs of your best projects." },
    { icon: Sun, title: "Seasonal Campaigns", desc: "Capture spring/fall rush with targeted seasonal marketing strategies." },
    { icon: Star, title: "Houzz & Directory Optimization", desc: "Dominate home improvement directories where homeowners search." },
    { icon: Sparkles, title: "Social Media Marketing", desc: "Build your brand on Instagram, Facebook, and Pinterest with visual content." },
    { icon: BarChart3, title: "Lead Tracking & ROI", desc: "Know exactly which marketing channels generate your best projects." }
  ];

  const projectTypes = [
    "Lawn Care & Maintenance", "Landscape Design", "Hardscaping", "Irrigation Systems",
    "Tree Service", "Outdoor Living", "Commercial Landscaping", "Holiday Lighting"
  ];

  const serviceAreas = [
    "Dallas", "Fort Worth", "Plano", "Frisco", "McKinney", 
    "Allen", "Richardson", "Southlake", "Colleyville", "Highland Park",
    "University Park", "Preston Hollow", "Lakewood"
  ];

  const faqs = [
    { 
      q: "How can I get more landscaping leads online?", 
      a: "Landscaping leads come from multiple channels: Local SEO (ranking for 'landscaper near me'), Google Ads targeting homeowners, Houzz profile optimization, social media showcasing your work, and email marketing to past clients. We create a comprehensive strategy that generates leads year-round, not just during peak seasons." 
    },
    { 
      q: "What's the best way to showcase landscaping work online?", 
      a: "High-quality before/after photos are essential—we recommend professional photography for your best projects. Video walkthroughs, drone footage of large properties, and time-lapse installation videos perform exceptionally well on social media. We optimize all content for SEO and distribute across platforms." 
    },
    { 
      q: "How do I market landscaping services in the off-season?", 
      a: "The off-season is perfect for promoting holiday lighting installation, hardscaping projects, spring planning consultations, and design services. We run email campaigns to your customer database, create content targeting 'spring landscaping' searches (which peak in winter), and maintain your SEO presence year-round." 
    },
    { 
      q: "Should I focus on residential or commercial landscaping marketing?", 
      a: "It depends on your business goals. Residential typically has higher volume but lower project values. Commercial contracts offer steady recurring revenue. We can target both with different strategies—local SEO and social media for residential, LinkedIn and direct outreach for commercial property managers." 
    }
  ];

  const caseStudyResults = [
    { metric: "Monthly Leads", before: "25", after: "95", change: "+280%" },
    { metric: "Avg Project Value", before: "$2,800", after: "$4,500", change: "+60%" },
    { metric: "5-Star Reviews", before: "23", after: "156", change: "+578%" }
  ];

  const schemaMarkup = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        "name": "Landscaping Marketing & SEO Services Dallas",
        "provider": {
          "@type": "LocalBusiness",
          "name": "Digital Media Ninja",
          "address": { "@type": "PostalAddress", "addressLocality": "Dallas", "addressRegion": "TX", "addressCountry": "US" },
          "telephone": "(432) 934-9470"
        },
        "areaServed": serviceAreas.map(city => ({ "@type": "City", "name": city })),
        "description": "Specialized digital marketing for landscaping companies in Dallas-Fort Worth. Local SEO, social media marketing, and lead generation for lawn care and landscape design businesses.",
        "serviceType": "Landscaping Digital Marketing"
      },
      {
        "@type": "FAQPage",
        "mainEntity": faqs.map(faq => ({
          "@type": "Question",
          "name": faq.q,
          "acceptedAnswer": { "@type": "Answer", "text": faq.a }
        }))
      }
    ]
  };

  return (
    <div className="min-h-screen bg-black" data-testid="landscaping-marketing-page">
      <Helmet>
        <title>Landscaping Marketing Dallas | Lawn Care SEO | Digital Media Ninja</title>
        <meta name="description" content="Expert landscaping marketing and SEO services in Dallas-Fort Worth. Generate more leads for your lawn care or landscaping company. Showcase your work and book more projects." />
        <meta name="keywords" content="landscaping marketing Dallas, lawn care SEO, landscaper marketing DFW, landscape design marketing, lawn service advertising, landscaping lead generation, landscaper SEO Dallas" />
        <link rel="canonical" href="https://digitalmedianinja.com/landscaping-marketing" />
        <meta property="og:title" content="Landscaping Marketing Dallas | Lawn Care SEO" />
        <meta property="og:description" content="Expert landscaping marketing for Dallas contractors. Local SEO, social media, portfolio marketing." />
        <meta property="og:url" content="https://digitalmedianinja.com/landscaping-marketing" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(schemaMarkup)}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative pt-32 pb-20">
        <div className="absolute inset-0 bg-gradient-to-br from-green-600/10 via-transparent to-emerald-500/10"></div>
        <div className="container-custom relative z-10">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/30 rounded-full mb-6">
              <TreePine className="w-4 h-4 text-green-400" />
              <span className="text-green-400 text-sm font-medium">Landscaping Industry Experts</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Grow Your <span className="text-green-400">Landscaping Business</span> in Dallas
            </h1>
            
            <p className="text-lg text-gray-300 mb-8 max-w-2xl">
              Showcase your best work and attract high-value clients. We help Dallas landscapers and lawn care companies generate more leads and book bigger projects.
            </p>
            
            <div className="flex flex-wrap gap-4 mb-12">
              <BookConsultationButton meetingType="landscaping" size="lg">
                Get Free Landscaping Marketing Audit <ArrowRight className="w-5 h-5 ml-2" />
              </BookConsultationButton>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-green-400/30 text-green-400 hover:bg-green-400/10"
                onClick={() => window.location.href='/blog/how-to-market-a-landscaping-business'}
                data-testid="read-blog-btn"
              >
                <FileText className="w-5 h-5 mr-2" /> Read Our Full Guide
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white/20 text-white hover:bg-white/10"
                onClick={() => window.location.href='/guides/landscaping-marketing'}
                data-testid="read-playbook-btn"
              >
                <Download className="w-5 h-5 mr-2" /> Marketing Playbook
              </Button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {stats.map((stat, i) => (
                <div key={i} className="text-center p-4 bg-white/5 rounded-xl border border-white/10">
                  <stat.icon className="w-6 h-6 text-green-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{stat.value}</p>
                  <p className="text-xs text-gray-400">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-white text-center mb-4">Landscaping Marketing Services</h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">Comprehensive digital marketing designed to showcase your work and generate quality leads.</p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((svc, i) => (
              <Card key={i} className="bg-[#111] border-white/10 hover:border-green-400/30 transition-all">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-green-400/10 flex items-center justify-center mb-4">
                    <svc.icon className="w-6 h-6 text-green-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{svc.title}</h3>
                  <p className="text-gray-400 text-sm">{svc.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Project Types */}
      <section className="py-20 bg-gradient-to-b from-black to-[#0a0a0a]">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-white text-center mb-12">Landscaping Services We Market</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {projectTypes.map((type, i) => (
              <div key={i} className="p-4 bg-white/5 rounded-xl border border-white/10 text-center hover:border-green-400/30 transition-colors">
                <p className="text-white font-medium">{type}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Premium Areas */}
      <section className="py-20">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-white text-center mb-4">Premium DFW Neighborhoods We Target</h2>
          <p className="text-gray-400 text-center mb-12">We help you reach affluent homeowners in Dallas's best neighborhoods.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {serviceAreas.map((area, i) => (
              <div key={i} className="p-3 bg-white/5 rounded-xl border border-white/10 text-center hover:border-green-400/30 transition-colors">
                <p className="text-white font-medium text-sm">{area}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Study Results */}
      <section className="py-20 bg-gradient-to-b from-[#0a0a0a] to-black">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-white text-center mb-4">Real Landscaping Marketing Results</h2>
          <p className="text-gray-400 text-center mb-12">See how we helped a Dallas landscaper grow their business.</p>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {caseStudyResults.map((result, i) => (
              <div key={i} className="text-center p-8 bg-[#111] rounded-2xl border border-white/10">
                <p className="text-gray-400 mb-2">{result.metric}</p>
                <div className="flex items-center justify-center gap-4 mb-2">
                  <span className="text-gray-500 line-through">{result.before}</span>
                  <ArrowRight className="w-4 h-4 text-green-400" />
                  <span className="text-2xl font-bold text-white">{result.after}</span>
                </div>
                <p className="text-green-400 font-semibold">{result.change}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-white text-center mb-12">Landscaping Marketing FAQs</h2>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, i) => (
              <details key={i} className="group bg-[#111] rounded-xl border border-white/10">
                <summary className="p-6 cursor-pointer text-white font-medium flex items-center justify-between hover:bg-white/5">
                  {faq.q}
                  <span className="text-green-400 group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <div className="px-6 pb-6 text-gray-400">{faq.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-green-500/10 to-emerald-500/10">
        <div className="container-custom text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Ready to Grow Your Landscaping Business?</h2>
          <p className="text-gray-300 mb-8 max-w-2xl mx-auto">Get a free landscaping marketing audit and custom strategy to book more high-value projects.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <BookConsultationButton meetingType="landscaping" size="lg">
              <Calendar className="w-5 h-5 mr-2" /> Book Strategy Call
            </BookConsultationButton>
            <a href="tel:+14329349470">
              <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/10">
                Call (432) 934-9470
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandscapingMarketingPage;
